import { CertificatePreviewData } from "./certificatePlaceholders";
import { CERTIFICATE_SAMPLE_DATA, mergeClinicPreviewData } from "./certificateSampleData";
import { calculateAge, hasDisplayValue } from "../../../patient/utils/patientUtils";
import { LoggedInClinic, getLoggedInClinicFromSession, mergeLoggedInClinic } from "./getLoggedInClinic";

export function formatCertificateDate(value?: string | Date | null): string {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${date.getFullYear()}`;
}

export function inclusiveLeaveDays(start?: string, end?: string): string {
  if (!start || !end) return "";
  const from = new Date(start);
  const to = new Date(end);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return "";
  const days = Math.round((to.getTime() - from.getTime()) / 86400000) + 1;
  return String(Math.max(1, days));
}

function treatmentLabel(entry: any): string {
  return [entry?.treatment, entry?.type, entry?.subtype, entry?.stage]
    .filter(hasDisplayValue)
    .join(" ");
}

function flattenTreatments(items: any[]): any[] {
  if (!Array.isArray(items)) return [];
  return items.flatMap((item) => {
    if (Array.isArray(item?.treatments) && item.treatments.length > 0) {
      return item.treatments.map((treatment: any) => ({
        ...treatment,
        updatedAt: treatment?.updatedAt ?? item?.updatedAt,
        createdAt: treatment?.createdAt ?? item?.createdAt,
      }));
    }
    return [item];
  });
}

function latestTreatment(items: any[]): any | null {
  const list = flattenTreatments(items);
  if (list.length === 0) return null;
  return [...list].sort((a, b) => {
    const aTime = new Date(a?.updatedAt ?? a?.createdAt ?? 0).getTime();
    const bTime = new Date(b?.updatedAt ?? b?.createdAt ?? 0).getTime();
    return bTime - aTime;
  })[0];
}

function doctorLabel(clinic?: { drName?: string }): string {
  const name = String(clinic?.drName ?? "").trim();
  if (!name || name === "docto1 james") return "";
  return name.startsWith("Dr") ? name : `Dr. ${name}`;
}

export function buildCertificatePrintData(options: {
  patient?: any;
  clinic?: Partial<LoggedInClinic>;
  treatments?: any[];
  overrides?: Partial<CertificatePreviewData>;
}): CertificatePreviewData {
  const { patient, treatments = [], overrides } = options;
  const latest = latestTreatment(treatments);
  const treatment = treatmentLabel(latest);
  const age = patient?.dob ? calculateAge(patient.dob) : null;
  const year = new Date().getFullYear();
  const serial = String(patient?.regNo || patient?._id || "000001")
    .replace(/[^a-zA-Z0-9]/g, "")
    .slice(-6)
    .padStart(6, "0")
    .toUpperCase();

  const clinic = mergeLoggedInClinic(getLoggedInClinicFromSession(), options.clinic);
  const mergedClinic = mergeClinicPreviewData(CERTIFICATE_SAMPLE_DATA, clinic);
  const doctor = doctorLabel(clinic);

  return {
    ...mergedClinic,
    certificateNumber: `DC-${year}-${serial}`,
    certificateDate: formatCertificateDate(new Date()),
    patientName: String(patient?.fullName ?? "").trim() || "Patient",
    patientId: String(patient?.regNo ?? patient?._id ?? "").trim() || "—",
    patientAge: age != null ? String(age) : "—",
    patientGender: String(patient?.gender ?? "").trim() || "—",
    treatmentType: treatment || "dental treatment",
    treatmentDescription: treatment ? treatment.toLowerCase() : "dental treatment",
    treatmentDate: formatCertificateDate(latest?.updatedAt ?? latest?.createdAt ?? new Date()),
    reasonForLeave: "Dental treatment",
    leaveStartDate: formatCertificateDate(new Date()),
    leaveEndDate: formatCertificateDate(new Date()),
    leaveDays: "1",
    recommendationText: "Rest as advised and attend the next review appointment.",
    doctorName: doctor || mergedClinic.doctorName,
    doctorQualifications: doctor ? "" : mergedClinic.doctorQualifications,
    doctorDesignation: doctor ? "" : mergedClinic.doctorDesignation,
    slmcNumber: doctor ? "" : mergedClinic.slmcNumber,
    ...overrides,
  };
}
